import { cn } from '@/lib/utils';
import { ArcheReviewSummary, ArcheReviewDetail, ReviewFinding } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';

interface ReviewStatusProps {
  reviews: ArcheReviewSummary[];
  isLoading: boolean;
  className?: string;
}

const statusConfig = {
  pending: {
    icon: Clock,
    label: 'Pending',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
  },
  in_progress: {
    icon: Loader2,
    label: 'In Progress',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  approved: {
    icon: CheckCircle,
    label: 'Approved',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  rejected: {
    icon: XCircle,
    label: 'Rejected',
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
  },
};

export function ReviewStatus({ reviews, isLoading, className }: ReviewStatusProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading reviews...</span>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        No reviews yet. Save your Arche and request a review to publish.
      </div>
    );
  }

  const latestReview = reviews[0];
  const config = statusConfig[latestReview.status];
  const StatusIcon = config.icon;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        <StatusIcon
          className={cn(
            'h-5 w-5',
            config.color,
            latestReview.status === 'in_progress' && 'animate-spin',
          )}
        />
        <span className="font-medium">{config.label}</span>
        {latestReview.riskScore !== null && (
          <Badge
            variant="outline"
            className={cn(
              latestReview.riskScore < 30
                ? 'border-green-500 text-green-600'
                : latestReview.riskScore < 70
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-red-500 text-red-600',
            )}
          >
            Risk: {latestReview.riskScore}
          </Badge>
        )}
      </div>

      <div className="text-xs text-muted-foreground">
        {latestReview.reviewerType === 'ai' ? 'AI Review' : 'Human Review'} - Version{' '}
        {latestReview.version}
      </div>
    </div>
  );
}

interface ReviewDetailViewProps {
  review: ArcheReviewDetail;
  className?: string;
}

export function ReviewDetailView({ review, className }: ReviewDetailViewProps) {
  const config = statusConfig[review.status];
  const StatusIcon = config.icon;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-full', config.bgColor)}>
            <StatusIcon
              className={cn(
                'h-5 w-5',
                config.color,
                review.status === 'in_progress' && 'animate-spin',
              )}
            />
          </div>
          <div>
            <h3 className="font-semibold">{config.label}</h3>
            <p className="text-sm text-muted-foreground">
              {review.reviewerType === 'ai' ? 'AI Review' : 'Human Review'} - Version{' '}
              {review.version}
            </p>
          </div>
        </div>

        {review.riskScore !== null && (
          <div className="text-right">
            <div className="text-2xl font-bold">{review.riskScore}</div>
            <div className="text-xs text-muted-foreground">Risk Score</div>
          </div>
        )}
      </div>

      {/* Findings sections */}
      {review.securityFindings && review.securityFindings.findings.length > 0 && (
        <FindingsSection
          title="Security Findings"
          findings={review.securityFindings.findings}
          summary={review.securityFindings.summary}
        />
      )}

      {review.policyFindings && review.policyFindings.findings.length > 0 && (
        <FindingsSection
          title="Policy Findings"
          findings={review.policyFindings.findings}
          summary={review.policyFindings.summary}
        />
      )}

      {review.qualityFindings && review.qualityFindings.findings.length > 0 && (
        <FindingsSection
          title="Quality Findings"
          findings={review.qualityFindings.findings}
          summary={review.qualityFindings.summary}
        />
      )}

      {/* Human notes */}
      {review.humanNotes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Reviewer Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{review.humanNotes}</p>
          </CardContent>
        </Card>
      )}

      {/* No findings message */}
      {!review.securityFindings?.findings.length &&
        !review.policyFindings?.findings.length &&
        !review.qualityFindings?.findings.length &&
        review.status === 'approved' && (
          <Card className="bg-green-500/5 border-green-500/20">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-green-600">
                No issues found. Your Arche is approved for publishing.
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  );
}

interface FindingsSectionProps {
  title: string;
  findings: ReviewFinding[];
  summary: { total: number; critical: number; high: number; medium: number; low: number };
}

function FindingsSection({ title, findings, summary }: FindingsSectionProps) {
  const [isOpen, setIsOpen] = useState(summary.critical > 0 || summary.high > 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{title}</CardTitle>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {summary.critical > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {summary.critical} Critical
                    </Badge>
                  )}
                  {summary.high > 0 && (
                    <Badge className="bg-orange-500 text-xs">{summary.high} High</Badge>
                  )}
                  {summary.medium > 0 && (
                    <Badge className="bg-yellow-500 text-xs">{summary.medium} Medium</Badge>
                  )}
                  {summary.low > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {summary.low} Low
                    </Badge>
                  )}
                </div>
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            {findings.map((finding) => (
              <FindingItem key={finding.id} finding={finding} />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

interface FindingItemProps {
  finding: ReviewFinding;
}

const severityConfig = {
  critical: {
    icon: AlertCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
  },
  high: {
    icon: AlertTriangle,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
  medium: {
    icon: AlertTriangle,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
  },
  low: {
    icon: Info,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
};

function FindingItem({ finding }: FindingItemProps) {
  const config = severityConfig[finding.severity];
  const Icon = config.icon;

  return (
    <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
      <div className={cn('p-1.5 rounded-full h-fit', config.bgColor)}>
        <Icon className={cn('h-4 w-4', config.color)} />
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{finding.category}</span>
          <Badge variant="outline" className="text-xs capitalize">
            {finding.severity}
          </Badge>
        </div>
        <p className="text-sm">{finding.message}</p>
        {finding.location && (
          <p className="text-xs text-muted-foreground">Location: {finding.location}</p>
        )}
        {finding.suggestion && (
          <p className="text-xs text-muted-foreground mt-2">
            <span className="font-medium">Suggestion:</span> {finding.suggestion}
          </p>
        )}
      </div>
    </div>
  );
}
